<script setup lang="ts">
import {
    useContentSeo,
    useHead,
    useImage,
    useSeoMeta,
    useI18n,
    useClock,
    useDayjs,
    useRoute,
    useThemeLogo,
    useArticleSchema,
    toSchemaDate,
    computed,
} from '#imports'
import type { ImageOptions as NuxtImageOptions, ConfiguredImageProviders } from '@nuxt/image'
import { useHeroConfig } from '../../composables/hero/useHeroConfig'
import { AsyncMarkdownDocument as MarkdownDocument } from '@core/utils/asyncMarkdownDocument'
import type { BlogPostWithTree } from '@core/types/blog'
import BlogGameList from './GameList.vue'
import ProseA from './ProseA.vue'
import { nativeBlogComponents } from './blogProse'

type BlogImageOptions = Omit<NuxtImageOptions, 'provider'> & { provider?: keyof ConfiguredImageProviders }

const OG_IMAGE_WIDTH = 1200
const OG_IMAGE_HEIGHT = 630

const blogComponents = {
    ...nativeBlogComponents,
    'a': ProseA,
    'game-list': BlogGameList,
}

export interface NimbusBlogDetailsProps {
    post?: BlogPostWithTree | null
}

const props = defineProps<NimbusBlogDetailsProps>()

const { t } = useI18n()
const { imagePreset } = useHeroConfig()

const brandIcon = useThemeLogo('icon')

const img = useImage() as (src: string, modifiers?: NuxtImageOptions['modifiers'], options?: BlogImageOptions) => string

const dayjs = useDayjs()
const { clock } = useClock()
const now = dayjs(clock.value)

const publishedRelative = computed(() =>
    props.post ? dayjs(props.post.publish_at).from(now) : '')

const publishedDatetime = computed(() =>
    props.post ? toSchemaDate(props.post.publish_at) : '')

const relatedPosts = computed(() =>
    (props.post?.related ?? []).map(related => ({
        ...related,
        relativeDate: dayjs(related.publish_at).from(now),
        datetime: toSchemaDate(related.publish_at),
    })))

if (props.post) {
    const { getHead, getSeoMeta } = useContentSeo({
        metaTitle: props.post.seo_meta_title || props.post.title,
        metaDescription: props.post.seo_meta_description || props.post.subtitle,
    })

    const heroUrl = props.post.hero_url
    const ogImageUrl = heroUrl
        ? img(
                heroUrl,
                { width: OG_IMAGE_WIDTH, height: OG_IMAGE_HEIGHT, fit: 'cover' },
                { provider: 'cloudflare', preset: imagePreset },
            )
        : undefined

    useHead(getHead())
    useSeoMeta({
        ...getSeoMeta(),
        ...(ogImageUrl ? { ogImageUrl } : {}),
    })

    if (!useRoute().meta.preview) {
        useArticleSchema().addArticle({
            type: 'BlogPosting',
            headline: props.post.title,
            description: props.post.seo_meta_description || props.post.subtitle,
            image: ogImageUrl,
            datePublished: publishedDatetime.value,
        })
    }
}
</script>

<template>
    <div v-if="post" class="blog-container">
        <div class="blog">
            <div class="blog-heading-container">
                <div class="blog__date">
                    <div class="blog__label">
                        {{ post.label }}
                    </div>

                    <time class="blog__created" :datetime="publishedDatetime">
                        {{ publishedRelative }}
                    </time>
                </div>

                <NuxtLink to="/blog">
                    {{ t('nimbus.blogDetails.back') }}
                </NuxtLink>
            </div>

            <div class="blog-media-container">
                <div
                    class="blog__thumbnail"
                    :class="{ 'blog__thumbnail--fallback': !post.hero_url }"
                >
                    <NuxtImg
                        v-if="post.hero_url"
                        provider="cloudflare"
                        :preset="imagePreset"
                        :src="post.hero_url"
                        width="480"
                        height="480"
                        sizes="100vw md:480px"
                        loading="lazy"
                        decoding="async"
                    />

                    <NuxtImg
                        v-else-if="brandIcon"
                        class="blog__fallback-logo"
                        provider="cloudflare"
                        :preset="imagePreset"
                        :src="brandIcon.src"
                        :quality="brandIcon.quality"
                        alt=""
                        width="120"
                        height="120"
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <div class="other-news">
                    <div class="other-news__title">
                        {{ t('nimbus.blogDetails.otherNews') }}
                    </div>

                    <div
                        v-for="relatedPost in relatedPosts"
                        :key="relatedPost.slug"
                    >
                        <NuxtLink
                            :to="{ name: 'blog-slug', params: { slug: relatedPost.slug } }"
                        >
                            <div>{{ relatedPost.title }}</div>
                        </NuxtLink>

                        <time class="blog__created blog__created--post" :datetime="relatedPost.datetime">
                            {{ relatedPost.relativeDate }}
                        </time>
                    </div>
                </div>
            </div>

            <div class="blog__headline">
                <h1 class="blog__title">
                    {{ post.title }}
                </h1>

                <div class="blog__subtitle">
                    {{ post.subtitle }}
                </div>
            </div>

            <div class="blog__content">
                <MarkdownDocument :value="post.tree" :components="blogComponents" />
            </div>

            <div class="other-news other-news--mobile">
                <div class="other-news__title">
                    {{ t('nimbus.blogDetails.otherNews') }}
                </div>

                <div
                    v-for="relatedPost in relatedPosts"
                    :key="relatedPost.slug"
                >
                    <NuxtLink
                        :to="{ name: 'blog-slug', params: { slug: relatedPost.slug } }"
                    >
                        <div>{{ relatedPost.title }}</div>
                    </NuxtLink>

                    <time class="blog__created blog__created--post" :datetime="relatedPost.datetime">
                        {{ relatedPost.relativeDate }}
                    </time>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="blog-container">
        {{ t('nimbus.blogDetails.noBlogs') }}
    </div>
</template>

<style lang="scss" scoped>
.blog-container {
    display: grid;
    grid-template-columns: minmax(0, 800px);
    justify-content: center;
}

.blog-heading-container {
    grid-area: heading;

    display: flex;
    justify-content: space-between;
}

.blog-media-container {
    grid-area: media;

    display: grid;
    grid-template-columns: 2fr 1fr;
    justify-content: space-between;
    gap: 40px;

    margin-bottom: 10px;

    @media (max-width: $screen-md) {
        grid-template-columns: 1fr;

        .blog__thumbnail {
            max-width: unset;
        }

        .other-news {
            display: none;
        }
    }
}

.blog {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
        'heading heading'
        'media media'
        'headline headline'
        'content content';
    justify-content: center;
    gap: 10px;

    background-color: var(--panel-bg, $panel-color);
    padding: 20px;
    border-radius: $border-radius;

    @media (max-width: $screen-md) {
        grid-template-columns: 1fr;
        grid-template-areas:
            'heading'
            'media'
            'headline'
            'content'
            'links';
    }

    &__date {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__headline {
        grid-area: headline;

        display: flex;
        flex-direction: column;
    }

    &__title {
        margin: 0;
        font-family: inherit;
        font-size: 24px;
        font-weight: 600;
        line-height: inherit;
        color: inherit;
    }

    &__subtitle {
        font-weight: 500;
        color: inherit;
    }

    &__label {
        font-size: 12px;
        font-weight: 600;
        color: $badge-blog-color;
        background-color: $badge-blog-bg;
        border-radius: var(--border-radius, $border-radius);
        line-height: 1.4;
        height: min-content;
        padding: 2px 8px;
    }

    &__thumbnail {
        flex-grow: 1;
        max-width: 500px;

        > img {
            height: 100%;
            width: 100%;
            border-radius: $border-radius;

            max-width: 480px;
            max-height: 480px;
            object-fit: cover;
        }

        &--fallback {
            display: flex;
            align-items: center;
            justify-content: center;

            aspect-ratio: 1;
            max-width: 480px;
            border-radius: $border-radius;
            background-color: $panel-secondary-color;
        }

        > img.blog__fallback-logo {
            height: 30%;
            width: 30%;
            object-fit: contain;
        }
    }

    // :deep — comark functional components don't carry this scope id.
    // Child selectors only (not bare tags) so embedded GameTiles keep their own <p> styles.
    &__content {
        grid-area: content;
        word-break: break-word;

        :deep(.comark-content) {
            > :first-child {
                margin-top: 0;
            }

            > :last-child {
                margin-bottom: 0;
            }

            > p,
            > ul,
            > ol {
                line-height: 1.6;
            }

            > p,
            li > p {
                margin: 0 0 16px;
            }

            > h2,
            > h3 {
                margin: 30px 0 10px;
            }

            ul,
            ol {
                margin: 0 0 16px;
                padding-left: 24px;
            }

            li {
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            // Global ol style sets display:block and drops the hanging indent.
            ol li {
                display: list-item;

                &::before {
                    content: none;
                }
            }

            li :is(ul, ol) {
                margin: 8px 0 0;
            }

            :is(p, li, h2, h3) a {
                text-decoration: underline;
                text-underline-offset: 2px;
            }

            table {
                display: block;
                max-width: 100%;
                overflow-x: auto;
                margin: 0 0 16px;
                border-collapse: collapse;
            }

            th,
            td {
                border: 1px solid currentcolor;
                padding: 6px 10px;
                text-align: left;
                vertical-align: top;
            }

            th {
                font-weight: 700;
            }
        }
    }

    &__created {
        display: block;
        font-size: 12px;
        font-weight: 700;
        color: inherit;

        &--post {
            color: inherit;
            font-weight: 400;
        }
    }
}

.other-news {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &--mobile {
        @media (min-width: $screen-md) {
            display: none;
        }
    }

    &__title {
        font-weight: 600;
    }
}
</style>
